import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLocation } from 'react-router-dom';
import { 
  Plus, Search, Edit, Trash2, MapPin, Users, Building2,
  RefreshCw, Eye, Filter, Download, School, AlertTriangle, User, DollarSign
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, Label, Select, Textarea } from '../../components/ui/Form';
import { Badge } from '../../components/ui/Badge';
import { useToast } from '../../contexts/ToastContext';
import { schoolsAPI, lookupDataAPI, schoolMealCostAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { collectAllPaginatedResults } from '../../utils/pagination';

const schoolSchema = z.object({
  school_name: z.string().min(1, 'School name is required'),
  school_code: z.string().min(1, 'School code is required'),
  branch_id: z.string().min(1, 'Please select a branch'),
  address: z.string().min(1, 'Address is required'),
  principal_name: z.string().min(1, 'Principal name is required'),
  principal_email: z.string().email('Invalid email address'),
  principal_phone: z.string().min(1, 'Principal phone is required'),
  total_students: z.string().min(1, 'Student count is required'),
  class_1_students: z.string().optional(),
  class_2_students: z.string().optional(),
  class_3_students: z.string().optional(),
  class_4_students: z.string().optional(),
  class_5_students: z.string().optional(),
  class_6_students: z.string().optional(),
  class_7_students: z.string().optional(),
  class_8_students: z.string().optional(),
  class_9_students: z.string().optional(),
  class_10_students: z.string().optional(),
});

const SchoolsManagement = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [schools, setSchools] = useState([]);
  const [branches, setBranches] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingSchool, setEditingSchool] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBranch, setFilterBranch] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [viewingSchool, setViewingSchool] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, schoolId: null, schoolName: '' });
  const [showMealCostForm, setShowMealCostForm] = useState(false);
  const [selectedSchoolForMealCost, setSelectedSchoolForMealCost] = useState(null);
  const [mealCosts, setMealCosts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);

  const defaultValues = {
    school_name: '',
    school_code: '',
    branch_id: user?.branch_id || '',
    address: '',
    principal_name: '',
    principal_email: '',
    principal_phone: '',
    total_students: '',
    class_1_students: '',
    class_2_students: '',
    class_3_students: '',
    class_4_students: '',
    class_5_students: '',
    class_6_students: '',
    class_7_students: '',
    class_8_students: '',
    class_9_students: '',
    class_10_students: '',
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    resolver: zodResolver(schoolSchema),
    defaultValues,
  });

  const selectedBranch = watch('branch_id');

  // Simple data loading function
  const loadData = async (search = '') => {
    try {
      setLoading(true);
      console.log('🌐 Making API calls for schools...');

      const [schoolsResult, branchesResponse] = await Promise.all([
        collectAllPaginatedResults(async (skip, limit) => {
          const response = await schoolsAPI.getSchools({ search, skip, limit });
          return {
            items: response?.schools || [],
            total: response?.total || 0,
          };
        }),
        lookupDataAPI.getBranches()
      ]);

      console.log('✅ Schools API Response:', schoolsResult);
      console.log('✅ Branches API Response:', branchesResponse);

      const mappedSchools = schoolsResult.items || [];
      console.log('📊 Mapped schools:', mappedSchools);
      setSchools(mappedSchools);

      // Map branches data
      let mappedBranches = [];
      if (Array.isArray(branchesResponse)) {
        mappedBranches = branchesResponse;
      } else if (branchesResponse?.data?.branches) {
        mappedBranches = branchesResponse.data.branches;
      } else if (branchesResponse?.branches) {
        mappedBranches = branchesResponse.branches;
      } else if (branchesResponse?.data) {
        mappedBranches = Array.isArray(branchesResponse.data) ? branchesResponse.data : [];
      }

      console.log('📊 Mapped branches:', mappedBranches);
      setBranches(mappedBranches);

      // Set user's branch as default filter if available
      if (user?.branch_id && !filterBranch) {
        setFilterBranch(user.branch_id);
        reset({ ...watch(), branch_id: user.branch_id });
      }

    } catch (error) {
      console.error('❌ Failed to load data:', error);
      toast.error('Failed to load data', error.message);
    } finally {
      setLoading(false);
    }
  };

  const getActiveBranches = () => {
    const activeBranches = branches.filter(branch => branch.is_active);
    if (editingSchool?.branch_id) {
      const currentBranch = branches.find(branch => branch.id === editingSchool.branch_id);
      if (currentBranch && !currentBranch.is_active) {
        return [currentBranch, ...activeBranches];
      }
    }
    return activeBranches;
  };

  // Load data on component mount and when the search term changes
  useEffect(() => {
    console.log('🚀 Schools component mounted or location changed:', location.pathname);
    loadData(searchTerm);
  }, [location.pathname, searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterBranch]);

  const refreshData = async () => {
    setRefreshing(true);
    try {
      console.log('🔄 Manual refresh triggered for schools');
      await loadData(searchTerm);
      toast.success('Data refreshed successfully');
    } catch (error) {
      toast.error('Failed to refresh data');
    } finally {
      setRefreshing(false);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      // Build students_by_class object from simple fields
      const studentsByClass = {};
      for (let i = 1; i <= 10; i++) {
        const fieldName = `class_${i}_students`;
        const studentCount = data[fieldName];
        if (studentCount && parseInt(studentCount) > 0) {
          studentsByClass[`Class ${i}`] = parseInt(studentCount);
        }
      }

      const schoolData = {
        school_name: data.school_name,
        school_code: data.school_code,
        branch_id: data.branch_id,
        address: data.address,
        principal_name: data.principal_name,
        principal_email: data.principal_email,
        principal_phone: data.principal_phone,
        total_students: parseInt(data.total_students),
        students_by_class: studentsByClass,
      };

      console.log('📝 Submitting school data:', schoolData);

      if (editingSchool) {
        await schoolsAPI.updateSchool(editingSchool.id, schoolData);
        toast.success('School updated successfully!');
      } else {
        await schoolsAPI.createSchool(schoolData);
        toast.success('School created successfully!');
      }
      
      setShowForm(false);
      setEditingSchool(null);
      reset();
      await loadData(searchTerm);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to save school';
      toast.error('Failed to save school', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (school) => {
    setEditingSchool(school);
    
    // Extract class-wise students from students_by_class object
    const classFields = {};
    for (let i = 1; i <= 10; i++) {
      const className = `Class ${i}`;
      classFields[`class_${i}_students`] = school.students_by_class?.[className]?.toString() || '';
    }
    
    reset({
      school_name: school.school_name,
      school_code: school.school_code || '',
      branch_id: school.branch_id,
      address: school.address,
      principal_name: school.principal_name,
      principal_email: school.principal_email,
      principal_phone: school.principal_phone,
      total_students: school.total_students?.toString() || '',
      ...classFields,
    });
    setShowForm(true);
  };

  const handleAddNew = () => {
    setEditingSchool(null);
    reset(defaultValues);
    setShowForm(true);
  };

  const handleDelete = (school) => {
    setDeleteConfirm({ show: true, schoolId: school.id, schoolName: school.school_name });
  };

  const confirmDelete = async () => {
    try {
      await schoolsAPI.deleteSchool(deleteConfirm.schoolId);
      toast.success('School deleted successfully!');
      await loadData(searchTerm);
      setDeleteConfirm({ show: false, schoolId: null, schoolName: '' });
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to delete school';
      toast.error('Failed to delete school', errorMessage);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirm({ show: false, schoolId: null, schoolName: '' });
  };

  const handleView = (school) => {
    setViewingSchool(school);
  };

  // Meal Cost Management Functions
  const loadMealCosts = async (schoolId) => {
    try {
      const costs = await schoolMealCostAPI.getMealCosts(schoolId);
      setMealCosts(costs || []);
    } catch (error) {
      console.error('Failed to load meal costs:', error);
      setMealCosts([]);
    }
  };

  const handleManageMealCost = (school) => {
    setSelectedSchoolForMealCost(school);
    loadMealCosts(school.id);
    setShowMealCostForm(true);
  };

  const filteredSchools = useMemo(() => {
    return schools.filter(school => {
      const matchesSearch = (school.school_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (school.principal_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (school.contact_person || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesBranch = !filterBranch || school.branch_id === filterBranch;
      return matchesSearch && matchesBranch;
    });
  }, [schools, searchTerm, filterBranch]);

  const totalPages = Math.max(1, Math.ceil(filteredSchools.length / itemsPerPage));
  const paginatedSchools = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredSchools.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredSchools, currentPage, itemsPerPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Schools Management</h1>
            <p className="text-gray-600 mt-2">Manage schools and educational institutions</p>
          </div>
          <div className="mt-4 sm:mt-0 flex items-center space-x-3">
            <Button onClick={handleAddNew}>
              <Plus size={16} className="mr-2" />
              Add School
            </Button>
            <Button variant="outline" size="sm" onClick={refreshData} disabled={refreshing}>
              <RefreshCw size={16} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-blue-600">{schools.length}</p>
                  <p className="text-sm text-gray-600">Total Schools</p>
                </div>
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <School className="text-blue-600" size={20} />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-green-600">
                    {schools.reduce((sum, s) => sum + (s.total_students || 0), 0)}
                  </p>
                  <p className="text-sm text-gray-600">Total Students</p>
                </div>
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Users className="text-green-600" size={20} />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-purple-600">
                    {new Set(schools.map(s => s.branch_id)).size}
                  </p>
                  <p className="text-sm text-gray-600">Branches Covered</p>
                </div>
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Building2 className="text-purple-600" size={20} />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* School Form */}
        {showForm && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-1"
          >
            <Card>
              <CardHeader>
                <CardTitle>{editingSchool ? 'Edit School' : 'Add New School'}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div>
                    <Label htmlFor="school_name">School Name</Label>
                    <Controller
                      name="school_name"
                      control={control}
                      render={({ field }) => (
                        <Input
                          placeholder="Enter school name"
                          {...field}
                          error={errors.school_name?.message}
                        />
                      )}
                    />
                  </div>

                  <div>
                    <Label htmlFor="school_code">School Code</Label>
                    <Controller
                      name="school_code"
                      control={control}
                      render={({ field }) => (
                        <Input
                          placeholder="Enter school code (e.g., SCH001)"
                          disabled={!!editingSchool}
                          {...field}
                          error={errors.school_code?.message}
                        />
                      )}
                    />
                  </div>

                  <div>
                    <Label htmlFor="branch_id">Branch</Label>
                    <Controller
                      name="branch_id"
                      control={control}
                      render={({ field }) => (
                        <Select {...field} error={errors.branch_id?.message}>
                          <option value="">Select Branch</option>
                          {getActiveBranches().map(branch => (
                            <option key={branch.id} value={branch.id}>
                              {branch.branch_name}
                            </option>
                          ))}
                        </Select>
                      )}
                    />
                  </div>

                  <div>
                    <Label htmlFor="address">Address</Label>
                    <Controller
                      name="address"
                      control={control}
                      render={({ field }) => (
                        <Textarea
                          placeholder="Enter school address"
                          {...field}
                        />
                      )}
                    />
                  </div>

                  <div>
                    <Label htmlFor="principal_name">Principal Name</Label>
                    <Controller
                      name="principal_name"
                      control={control}
                      render={({ field }) => (
                        <Input
                          placeholder="Enter principal name"
                          {...field}
                          error={errors.principal_name?.message}
                        />
                      )}
                    />
                  </div>

                  <div>
                    <Label htmlFor="principal_email">Principal Email</Label>
                    <Controller
                      name="principal_email"
                      control={control}
                      render={({ field }) => (
                        <Input
                          type="email"
                          placeholder="Enter principal email"
                          {...field}
                          error={errors.principal_email?.message}
                        />
                      )}
                    />
                  </div>

                  <div>
                    <Label htmlFor="principal_phone">Principal Phone</Label>
                    <Controller
                      name="principal_phone"
                      control={control}
                      render={({ field }) => (
                        <Input
                          placeholder="Enter principal phone"
                          {...field}
                          error={errors.principal_phone?.message}
                        />
                      )}
                    />
                  </div>

                  <div>
                    <Label htmlFor="total_students">Total Students</Label>
                    <Controller
                      name="total_students"
                      control={control}
                      render={({ field }) => (
                        <Input
                          type="number"
                          placeholder="Enter total student count"
                          {...field}
                          error={errors.total_students?.message}
                        />
                      )}
                    />
                  </div>

                  <div>
                    <Label>Students by Class (Optional)</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(classNum => (
                        <div key={classNum}>
                          <Label htmlFor={`class_${classNum}_students`} className="text-xs text-gray-600">
                            Class {classNum}
                          </Label>
                          <Controller
                            name={`class_${classNum}_students`}
                            control={control}
                            render={({ field }) => (
                              <Input
                                type="number"
                                placeholder="0"
                                {...field}
                                className="text-sm"
                              />
                            )}
                          />
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Enter number of students for each class
                    </p>
                  </div>

                  <div className="flex space-x-3">
                    <Button
                      type="submit"
                      className="flex-1"
                      loading={loading}
                    >
                      {editingSchool ? 'Update' : 'Create'} School
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowForm(false);
                        setEditingSchool(null);
                        reset();
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Schools List */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className={`${showForm ? 'lg:col-span-2' : 'lg:col-span-3'}`}
        >
          <Card>
            <CardHeader>
              <CardTitle>Schools</CardTitle>
              <div className="flex flex-col sm:flex-row gap-3 mt-4">
                <div className="flex-1">
                  <Input
                    placeholder="Search schools..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                </div>
                <Select
                  value={filterBranch}
                  onChange={(e) => setFilterBranch(e.target.value)}
                  className="sm:w-48"
                >
                  <option value="">All Branches</option>
                  {branches.map(branch => (
                    <option key={branch.id} value={branch.id}>
                      {branch.branch_name}
                    </option>
                  ))}
                </Select>
                <Button variant="outline" size="sm">
                  <Download size={16} className="mr-2" />
                  Export
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {paginatedSchools.length === 0 ? (
                  <div className="text-center py-8">
                    <School className="text-gray-400 mx-auto mb-2" size={48} />
                    <p className="text-gray-500">No schools found</p>
                  </div>
                ) : (
                  paginatedSchools.map((school, index) => {
                    const branch = branches.find(b => b.id === school.branch_id);
                    return (
                      <motion.div
                        key={school.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.3 + index * 0.05 }}
                        className="border border-gray-100 rounded-lg p-4 hover:bg-gray-50"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <h3 className="font-semibold text-gray-900">{school.school_name}</h3>
                              <Badge variant="info">
                                {branch?.branch_name || 'Unknown Branch'}
                              </Badge>
                            </div>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600">
                              <div className="flex items-center">
                                <MapPin size={14} className="mr-1" />
                                {school.address}
                              </div>
                              <div className="flex items-center">
                                <Users size={14} className="mr-1" />
                                {school.total_students || 0} students
                              </div>
                              <div className="flex items-center">
                                <User size={14} className="mr-1" />
                                {school.principal_name || school.contact_person || 'N/A'}
                              </div>
                              <div className="flex items-center">
                                <Building2 size={14} className="mr-1" />
                                {school.principal_phone || school.contact_phone || 'N/A'}
                              </div>
                            </div>

                            <div className="mt-2 text-xs text-gray-500">
                              Email: {school.principal_email || school.contact_email || 'N/A'}
                            </div>
                          </div>

                          <div className="flex items-center space-x-2 ml-4">
                            <Button size="sm" variant="ghost" onClick={() => handleView(school)}>
                              <Eye size={14} />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleManageMealCost(school)}>
                              <DollarSign size={14} />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleEdit(school)}>
                              <Edit size={14} />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDelete(school)}>
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })
                )}
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-t border-gray-100 pt-4 mt-4 gap-3">
                <p className="text-sm text-gray-500">
                  Showing {filteredSchools.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, filteredSchools.length)} of {filteredSchools.length} schools
                </p>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-gray-600">Page {currentPage} of {totalPages}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* School Details Modal */}
      {viewingSchool && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setViewingSchool(null)}
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-bold">{viewingSchool.school_name}</h2>
              <Button variant="ghost" size="sm" onClick={() => setViewingSchool(null)}>
                ×
              </Button>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Branch</p>
                <p className="font-medium">
                  {branches.find(b => b.id === viewingSchool.branch_id)?.branch_name || 'Unknown'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Students</p>
                <p className="font-medium">{viewingSchool.total_students || 0}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Address</p>
                <p className="font-medium">{viewingSchool.address}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Principal Name</p>
                <p className="font-medium">{viewingSchool.principal_name || viewingSchool.contact_person || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Principal Phone</p>
                <p className="font-medium">{viewingSchool.principal_phone || viewingSchool.contact_phone || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Principal Email</p>
                <p className="font-medium">{viewingSchool.principal_email || viewingSchool.contact_email || 'N/A'}</p>
              </div>
              
              {/* Calculate total daily meal cost */}
              {(() => {
                let totalDailyMealCost = 0;
                if (viewingSchool.students_by_class && viewingSchool.meal_costs_by_class) {
                  Object.entries(viewingSchool.students_by_class).forEach(([className, students]) => {
                    const mealCost = viewingSchool.meal_costs_by_class[className] || 0;
                    totalDailyMealCost += students * mealCost;
                  });
                }
                return (
                  <>
                    <div>
                      <p className="text-sm text-gray-500">Daily Meal Cost Total</p>
                      <p className="font-medium">Rs {totalDailyMealCost.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Monthly Meal Cost (22 days)</p>
                      <p className="font-medium">Rs {(totalDailyMealCost * 22).toFixed(2)}</p>
                    </div>
                  </>
                );
              })()}
            </div>
            
            {/* Students and Meal Costs by Class */}
            {(viewingSchool.students_by_class && Object.keys(viewingSchool.students_by_class).length > 0) && (
              <div className="mt-4">
                <p className="text-sm text-gray-500 mb-2">Students and Meal Costs by Class</p>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="space-y-2 text-sm">
                    {Object.entries(viewingSchool.students_by_class).map(([className, students]) => {
                      const mealCost = viewingSchool.meal_costs_by_class?.[className] || 0;
                      const dailyCost = students * mealCost;
                      return (
                        <div key={className} className="flex justify-between items-center border-b border-gray-200 pb-1">
                          <div className="flex-1">
                            <span className="font-medium">{className}:</span>
                            <span className="ml-2 text-gray-600">{students} students</span>
                          </div>
                          <div className="text-right">
                            <div className="font-medium">Rs {mealCost.toFixed(2)}/student</div>
                            <div className="text-xs text-gray-500">Rs {dailyCost.toFixed(2)}/day</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={cancelDelete}
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center mb-4">
              <AlertTriangle className="text-red-600 mr-3" size={24} />
              <h3 className="text-lg font-bold text-gray-900">Delete School</h3>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-600">
                Are you sure you want to delete the school <strong>"{deleteConfirm.schoolName}"</strong>?
              </p>
              <p className="text-sm text-gray-500 mt-2">
                This action cannot be undone and will also delete all associated data including meal records and attendance.
              </p>
            </div>
            
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={cancelDelete}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDelete}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                Delete School
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Meal Cost Management Modal */}
      {showMealCostForm && selectedSchoolForMealCost && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowMealCostForm(false)}
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-xl font-bold">Meal Cost Management</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedSchoolForMealCost.school_name}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowMealCostForm(false)}>
                ×
              </Button>
            </div>
            
            <div className="space-y-4">
              {mealCosts.length === 0 ? (
                <div className="text-center py-8">
                  <DollarSign className="text-gray-400 mx-auto mb-2" size={48} />
                  <p className="text-gray-500 mb-4">No meal costs configured for this school</p>
                  <Button onClick={() => {
                    // Create initial meal cost entries for all classes
                    const initialMealCosts = [];
                    for (let i = 1; i <= 10; i++) {
                      initialMealCosts.push({
                        school_id: selectedSchoolForMealCost.id,
                        class_name: `Class ${i}`,
                        cost_per_student: 0,
                        is_active: true
                      });
                    }
                    setMealCosts(initialMealCosts);
                  }}>
                    Set Up Meal Costs
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="font-semibold text-gray-900">Class-wise Meal Costs</h3>
                    <Button size="sm" variant="outline" onClick={async () => {
                      await loadMealCosts(selectedSchoolForMealCost.id);
                      toast.success('Meal costs refreshed');
                    }}>
                      <RefreshCw size={14} className="mr-2" />
                      Refresh
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {mealCosts.map((mealCost, index) => (
                      <div key={mealCost.id || index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <h4 className="font-medium text-gray-900">{mealCost.class_name}</h4>
                          <Badge variant={mealCost.is_active ? 'success' : 'error'}>
                            {mealCost.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                        
                        <div className="space-y-2">
                          <div>
                            <Label className="text-xs text-gray-600">Cost per Student (Rs)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={mealCost.cost_per_student || ''}
                              onChange={(e) => {
                                const updatedCosts = [...mealCosts];
                                updatedCosts[index].cost_per_student = parseFloat(e.target.value) || 0;
                                setMealCosts(updatedCosts);
                              }}
                              placeholder="Enter cost per student"
                            />
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`active-${mealCost.id || index}`}
                              checked={mealCost.is_active}
                              onChange={(e) => {
                                const updatedCosts = [...mealCosts];
                                updatedCosts[index].is_active = e.target.checked;
                                setMealCosts(updatedCosts);
                              }}
                              className="rounded"
                            />
                            <Label htmlFor={`active-${mealCost.id || index}`} className="text-sm">
                              Active
                            </Label>
                          </div>
                          
                          <div className="flex space-x-2">
                            <Button
                              size="sm"
                              onClick={async () => {
                                try {
                                  if (mealCost.id) {
                                    await schoolMealCostAPI.updateMealCost(mealCost.id, {
                                      cost_per_student: mealCost.cost_per_student,
                                      is_active: mealCost.is_active
                                    });
                                    toast.success(`${mealCost.class_name} meal cost updated`);
                                  } else {
                                    const newMealCost = await schoolMealCostAPI.createMealCost({
                                      school_id: selectedSchoolForMealCost.id,
                                      class_name: mealCost.class_name,
                                      cost_per_student: mealCost.cost_per_student,
                                      is_active: mealCost.is_active
                                    });
                                    const updatedCosts = [...mealCosts];
                                    updatedCosts[index] = newMealCost;
                                    setMealCosts(updatedCosts);
                                    toast.success(`${mealCost.class_name} meal cost created`);
                                  }
                                } catch (error) {
                                  toast.error('Failed to save meal cost', error.message);
                                }
                              }}
                              disabled={!mealCost.cost_per_student || mealCost.cost_per_student <= 0}
                            >
                              {mealCost.id ? 'Update' : 'Create'}
                            </Button>
                            
                            {mealCost.id && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={async () => {
                                  try {
                                    await schoolMealCostAPI.deleteMealCost(mealCost.id);
                                    const updatedCosts = mealCosts.filter(mc => mc.id !== mealCost.id);
                                    setMealCosts(updatedCosts);
                                    toast.success(`${mealCost.class_name} meal cost deleted`);
                                  } catch (error) {
                                    toast.error('Failed to delete meal cost', error.message);
                                  }
                                }}
                              >
                                Delete
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex justify-end space-x-3 pt-4 border-t">
                    <Button variant="outline" onClick={() => setShowMealCostForm(false)}>
                      Close
                    </Button>
                    <Button onClick={async () => {
                      try {
                        // Save all meal costs
                        const savePromises = mealCosts.map(async (mealCost) => {
                          if (mealCost.cost_per_student && mealCost.cost_per_student > 0) {
                            if (mealCost.id) {
                              return schoolMealCostAPI.updateMealCost(mealCost.id, {
                                cost_per_student: mealCost.cost_per_student,
                                is_active: mealCost.is_active
                              });
                            } else {
                              return schoolMealCostAPI.createMealCost({
                                school_id: selectedSchoolForMealCost.id,
                                class_name: mealCost.class_name,
                                cost_per_student: mealCost.cost_per_student,
                                is_active: mealCost.is_active
                              });
                            }
                          }
                        });
                        
                        await Promise.all(savePromises);
                        await loadMealCosts(selectedSchoolForMealCost.id);
                        toast.success('All meal costs saved successfully');
                      } catch (error) {
                        toast.error('Failed to save some meal costs', error.message);
                      }
                    }}>
                      Save All
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default SchoolsManagement;
